import { api } from '../api/client'
import type { ConfirmCheck } from './approval'
import type { PnL } from './pnl'

const BASE = '/api/campaign-desk'

export type DeskTitle = { slug: string; name: string; enabled: boolean }

export type DeskSettings = {
  agent_on: boolean
  contact_cap_days: number
  email_cost_per_1000_cents: number
  report_to: string | null
  updated_by: string | null
  updated_at: string | null
  titles: DeskTitle[]
  /** Newest first, from the audit log: {setting: [before, after]}. */
  history: { by: string | null; at: string | null; changes: Record<string, [unknown, unknown]> }[]
}
export type SettingsPatch = Partial<Pick<DeskSettings, 'agent_on' | 'contact_cap_days' | 'email_cost_per_1000_cents' | 'report_to'>>

export type DeskStatus =
  | 'queued' | 'drafting' | 'ready' | 'changes_requested' | 'approved' | 'running' | 'paused'
  | 'completed' | 'stopped' | 'rejected' | 'failed'

export type Report = {
  expected: Partial<PnL>
  actual: PnL
  by_method: Record<string, { orders: number; paid_cents: number; list_cents: number }>
  by_wave: { wave: number; sent: number; opened: number; clicked: number }[]
  narrative: { summary: string; what_worked: string; what_id_change: string }
  orders: number
  expected_orders: number | null
  recipients: number
  closed_at: string
}

export type DeskCampaign = {
  id: number
  publication_slug: string
  origin: string
  brief: string
  article_id: number | null
  status: DeskStatus
  current_version: number
  budget_cents: number | null
  instruction: string | null
  campaign_id: number | null
  segment_id: number | null
  promo_code: string | null
  approved_by: string | null
  approved_at: string | null
  stopped_by: string | null
  stopped_at: string | null
  stop_reason: string | null
  paused_reason: string | null
  paused_fire_at: string | null
  report: Report | null
  error: string | null
  created_by: string
  created_at: string | null
  updated_at: string | null
  /** Set when one brief started a campaign on every active title: the first campaign's id. */
  group_id: number | null
  /** An admin switched the desk's contact limits off for this campaign. */
  ignore_limits: boolean
  ignore_limits_by: string | null
  ignore_limits_at: string | null
}

/** A list row: the summary plus the current version's figures. */
export type ListRow = DeskCampaign & {
  audience_count: number | null
  expected_net_cents: number | null
  actual_net_cents: number | null
}

export type EmailCopy = {
  subject: string
  alt_subjects: string[]
  preheader: string
  headline: string
  paragraphs: string[]
  cta_label: string
  cta_target: 'article' | 'join' | 'app'
  send_at: string
  /** What the lead photograph should show; null for none. The desk finds and checks the picture. */
  photo_subject?: string | null
}

export type FollowUpTarget = 'non_openers' | 'non_clickers' | 'clicked_not_bought' | 'not_converted'
export type FollowUp = {
  day_offset: number
  target: FollowUpTarget
  intent: string
  subject: string
  paragraphs: string[]
}

export type AudienceComputed = {
  count: number
  excluded: { reason: string; count: number }[]
  /** People the contact limits would have left out, kept in because the limits are off. */
  limits_ignored?: { reason: string; count: number }[]
  sample: { id: number; first_name: string | null; last_name: string | null; email: string | null }[]
}

export type EmailPreview = {
  subject: string
  preheader: string
  html_body: string
  text_body: string
  variable_defaults?: Record<string, string>
}

export type Package = {
  reason: string
  audience_definition: unknown
  email: EmailCopy
  follow_ups: FollowUp[]
  article_id: number | null
  promo_code: string | null
  budget_cents: number
  expected: { conversions: number; conversion_rate_basis: string; plan_id: 'monthly' | 'annual'; discount_cents_per_conversion: number }
  risks: string[]
  /** The agent's answer to the change request that produced this version; empty on a first draft. */
  reply_to_operator?: string
  computed?: {
    audience?: AudienceComputed
    pnl?: PnL
    email_preview?: EmailPreview
    photo?: PhotoPick | null
    photo_options?: PhotoPick[]
    photo_note?: string
    photo_subject?: string | null
  }
}

/** A photograph a model looked at for this email: how well it fits (0-3) and what it shows. */
export type PhotoPick = {
  url: string
  fit: number
  reason: string
  credit: string | null
  alt: string
  source_title: string | null
  chosen_by?: string
}

export type VersionChange = { what: string; before: string; after: string }

export type Version = {
  version: number
  package: Package
  instruction: string | null
  model: string | null
  input_tokens: number | null
  output_tokens: number | null
  created_at: string | null
  /** What this version changed from the one before it. */
  changes?: VersionChange[]
}

export type DeskEvent = { kind: string; detail: Record<string, unknown> | null; by: string; at: string | null }
export type DeskCost = { kind: string; amount_cents: number; is_estimate: boolean; source: string | null; occurred_at: string | null }
export type Conversion = {
  order_id: number; subscriber_id: number; channel: string; method: string
  paid_cents: number; list_cents: number; ordered_at: string | null
}

export type Detail = {
  campaign: DeskCampaign
  version: Version | null
  events: DeskEvent[]
  costs: DeskCost[]
  spent_cents: number
  conversions: Conversion[]
  frozen_count: number
  /** The linked Campaign's status (draft, scheduled, running, completed, cancelled, failed), once approved. */
  campaign_status: string | null
}

export type ArticleHit = { id: number; title: string; url: string | null; post_date: string | null }

const enc = encodeURIComponent

export const getSettings = () => api.get<DeskSettings>(`${BASE}/settings`)
export const patchSettings = (body: SettingsPatch) => api.patch<DeskSettings>(`${BASE}/settings`, body)

export function listCampaigns(f: { status?: string; title?: string; group?: number | null } = {}) {
  const q = new URLSearchParams()
  if (f.status) q.set('status', f.status)
  if (f.title) q.set('title', f.title)
  if (f.group) q.set('group_id', String(f.group))
  const s = q.toString()
  return api.get<{ campaigns: ListRow[] }>(`${BASE}/campaigns${s ? `?${s}` : ''}`)
}
export const getCampaign = (id: number) => api.get<Detail>(`${BASE}/campaigns/${id}`)
type BriefFields = { brief: string; budget_cents?: number | null; ignore_limits?: boolean }
export const createBrief = (body: BriefFields & { publication_slug: string; article_id?: number | null }) =>
  api.post<Detail>(`${BASE}/campaigns`, body)
/** One brief on every title open on the desk: one campaign per title, sharing group_id. */
export const createBriefGroup = (body: BriefFields) =>
  api.post<{ group_id: number; campaigns: DeskCampaign[] }>(`${BASE}/campaigns`, { ...body, every_title: true })
export const findArticles = (title: string, q: string) =>
  api.get<{ articles: ArticleHit[] }>(`${BASE}/articles?title=${enc(title)}&q=${enc(q)}`)

export const requestChanges = (id: number, text: string) => api.post<Detail>(`${BASE}/campaigns/${id}/changes`, { text })
export const setLimits = (id: number, ignore_limits: boolean) =>
  api.post<Detail>(`${BASE}/campaigns/${id}/limits`, { ignore_limits })
export const reject = (id: number, text: string) => api.post<Detail>(`${BASE}/campaigns/${id}/reject`, { text })
export const editCopy = (id: number, body: { version: number; email: EmailCopy; follow_ups: FollowUp[] }) =>
  api.post<Detail>(`${BASE}/campaigns/${id}/edit`, body)
export const choosePhoto = (id: number, body: { version: number; url: string | null }) =>
  api.post<Detail>(`${BASE}/campaigns/${id}/photo`, body)
export const confirmPhrase = (id: number) => api.get<ConfirmCheck>(`${BASE}/campaigns/${id}/confirm-phrase`)
export const approve = (id: number, body: { version: number; budget_cents: number; send_at: string; confirm_token: string }) =>
  api.post<Detail>(`${BASE}/campaigns/${id}/approve`, body)
export const stop = (id: number, text: string) => api.post<Detail>(`${BASE}/campaigns/${id}/stop`, { text })
export const resume = (id: number, budget_cents?: number | null) =>
  api.post<Detail>(`${BASE}/campaigns/${id}/resume`, budget_cents == null ? {} : { budget_cents })
export const testSend = (id: number) => api.post<Record<string, unknown>>(`${BASE}/campaigns/${id}/test-send`)

/** True while the strategist still owes a draft (a change request is queued for it too): the page polls then. */
export const isDrafting = (s: DeskStatus | string | undefined): boolean => s === 'queued' || s === 'drafting' || s === 'changes_requested'
